/**
 * CommissionPreviewPage — Trackier-style CRUD list for `commission-preview`.
 *
 * Layout (top→bottom):
 *   - Topbar: title + "Run Preview" action button
 *   - Filter bar: campaign + publisher + amount
 *   - Preview panel: base / tier / adjustment / total (live calc)
 *   - Table: campaign, rule, calc, preview-amount, status
 *
 * Brand discipline (strict):
 *   - Accent: green-600 / green-700 / green-50 ONLY (no sky/emerald/amber)
 *   - Neutrals: white + zinc-200 / zinc-900
 *   - Typography: Inter (inherited)
 *   - Rounding: 6-8px
 *   - Icons: inline SVG (no external lib, no emoji)
 *   - No dark mode, no competitor names
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useCommissionPreviewList,
	useCommissionPreviewCreate,
} from "./hooks";
import {
	computePreview,
	formatMoneyCents,
	type CommissionPreviewRow,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — green-600 accent ONLY.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08)]";
const LABEL = "text-xs font-medium uppercase tracking-wide text-zinc-600";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-green-600 bg-green-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-green-700 hover:border-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50";
const PILL_APPROVED =
	"inline-flex items-center rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700";
const PILL_PENDING =
	"inline-flex items-center rounded-full border border-zinc-200 bg-white px-2 py-0.5 text-xs font-medium text-zinc-600";
const PILL_REJECTED =
	"inline-flex items-center rounded-full border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-700";
const TH =
	"border-b border-zinc-200 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600";
const TD = "border-b border-zinc-100 px-3 py-2.5 text-sm text-zinc-800";

// ---------------------------------------------------------------------------
// Inline SVG icons (stroke=currentColor).
// ---------------------------------------------------------------------------

function PlayIcon() {
	return (
		<svg
			aria-hidden="true"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<polygon points="6 4 20 12 6 20 6 4" />
		</svg>
	);
}

function FilterIcon() {
	return (
		<svg
			aria-hidden="true"
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<polygon points="22 3 2 3 10 12.5 10 19 14 21 14 12.5 22 3" />
		</svg>
	);
}

function CoinsIcon() {
	return (
		<svg
			aria-hidden="true"
			width="16"
			height="16"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<circle cx="8" cy="8" r="6" />
			<path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
			<path d="M7 6h1v4" />
			<path d="M16.71 13.88l.7.71-2.82 2.82" />
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusPill(status: string) {
	if (status === "approved") return PILL_APPROVED;
	if (status === "rejected") return PILL_REJECTED;
	return PILL_PENDING;
}

function rowCalcString(row: CommissionPreviewRow): string {
	const b = formatMoneyCents(row.base_amount_cents ?? 0);
	const t = formatMoneyCents(row.tier_amount_cents ?? 0);
	const a = formatMoneyCents(row.adjustment_cents ?? 0);
	return `${b} + ${t} + ${a}`;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CommissionPreviewPage() {
	const listQuery = useCommissionPreviewList();
	const createMut = useCommissionPreviewCreate();

	// Filters (client-side, over listed rows).
	const [filterCampaign, setFilterCampaign] = useState("");
	const [filterPublisher, setFilterPublisher] = useState("");
	const [filterAmount, setFilterAmount] = useState("");

	// Preview form (feeds create mutation).
	const [campaign, setCampaign] = useState("");
	const [publisher, setPublisher] = useState("");
	const [rule, setRule] = useState("flat");
	const [baseStr, setBaseStr] = useState("100.00");
	const [tierStr, setTierStr] = useState("0.00");
	const [adjStr, setAdjStr] = useState("0.00");

	const calc = useMemo(() => {
		const toCents = (s: string) => {
			const n = Number.parseFloat(s);
			if (!Number.isFinite(n)) return 0;
			return Math.round(n * 100);
		};
		return computePreview(toCents(baseStr), toCents(tierStr), toCents(adjStr));
	}, [baseStr, tierStr, adjStr]);

	const items = listQuery.data?.items ?? [];

	const filtered = useMemo(() => {
		const cNeedle = filterCampaign.trim().toLowerCase();
		const pNeedle = filterPublisher.trim().toLowerCase();
		const amtCents = (() => {
			const n = Number.parseFloat(filterAmount);
			return Number.isFinite(n) ? Math.round(n * 100) : null;
		})();
		return items.filter((row) => {
			if (cNeedle && !String(row.campaign ?? "").toLowerCase().includes(cNeedle))
				return false;
			if (pNeedle && !String(row.publisher ?? "").toLowerCase().includes(pNeedle))
				return false;
			if (amtCents !== null && row.total_cents !== amtCents) return false;
			return true;
		});
	}, [items, filterCampaign, filterPublisher, filterAmount]);

	const handleRunPreview = (e: FormEvent) => {
		e.preventDefault();
		if (!campaign.trim() || !publisher.trim()) return;
		createMut.mutate({
			campaign: campaign.trim(),
			publisher: publisher.trim(),
			rule: rule.trim() || "flat",
			base_amount_cents: calc.base_cents,
			tier_amount_cents: calc.tier_cents,
			adjustment_cents: calc.adjustment_cents,
		});
	};

	const canRun =
		!createMut.isPending && campaign.trim() !== "" && publisher.trim() !== "";

	return (
		<main
			data-testid="commission-preview-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8"
		>
			{/* Topbar */}
			<header className="flex items-center justify-between border-b border-zinc-200 pb-4">
				<div className="flex flex-col gap-1">
					<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
						Commissions
					</p>
					<h1 className="flex items-center gap-2 text-2xl font-semibold text-zinc-900">
						<span className="text-green-600">
							<CoinsIcon />
						</span>
						Commission Preview
					</h1>
				</div>
				<button
					type="button"
					onClick={handleRunPreview}
					className={BTN_PRIMARY}
					disabled={!canRun}
					data-testid="commission-preview-topbar-run"
				>
					<PlayIcon />
					{createMut.isPending ? "Running." : "Run Preview"}
				</button>
			</header>

			{/* Filter bar */}
			<section
				className={`${CARD} flex flex-col gap-3 p-4`}
				aria-label="Filter commission previews"
			>
				<div className="flex items-center gap-2 text-zinc-700">
					<FilterIcon />
					<h2 className="text-sm font-semibold text-zinc-800">Filters</h2>
				</div>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
					<div className="flex flex-col gap-1.5">
						<label htmlFor="filter-campaign" className={LABEL}>
							Campaign
						</label>
						<input
							id="filter-campaign"
							type="text"
							className={INPUT}
							placeholder="e.g. summer-sale"
							value={filterCampaign}
							onChange={(e) => setFilterCampaign(e.currentTarget.value)}
							data-testid="commission-preview-filter-campaign"
						/>
					</div>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="filter-publisher" className={LABEL}>
							Publisher
						</label>
						<input
							id="filter-publisher"
							type="text"
							className={INPUT}
							placeholder="e.g. pub-123"
							value={filterPublisher}
							onChange={(e) => setFilterPublisher(e.currentTarget.value)}
							data-testid="commission-preview-filter-publisher"
						/>
					</div>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="filter-amount" className={LABEL}>
							Amount (USD)
						</label>
						<input
							id="filter-amount"
							type="number"
							step="0.01"
							className={INPUT}
							placeholder="0.00"
							value={filterAmount}
							onChange={(e) => setFilterAmount(e.currentTarget.value)}
							data-testid="commission-preview-filter-amount"
						/>
					</div>
				</div>
			</section>

			{/* Preview panel (calculator + submit) */}
			<section
				className={`${CARD} p-5`}
				aria-label="Commission preview calculator"
			>
				<form
					onSubmit={handleRunPreview}
					data-testid="commission-preview-form"
					className="flex flex-col gap-4"
				>
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
						<div className="flex flex-col gap-1.5">
							<label htmlFor="cp-campaign" className={LABEL}>
								Campaign
							</label>
							<input
								id="cp-campaign"
								type="text"
								className={INPUT}
								placeholder="campaign slug"
								value={campaign}
								onChange={(e) => setCampaign(e.currentTarget.value)}
								disabled={createMut.isPending}
								data-testid="commission-preview-campaign-input"
							/>
						</div>
						<div className="flex flex-col gap-1.5">
							<label htmlFor="cp-publisher" className={LABEL}>
								Publisher
							</label>
							<input
								id="cp-publisher"
								type="text"
								className={INPUT}
								placeholder="publisher id"
								value={publisher}
								onChange={(e) => setPublisher(e.currentTarget.value)}
								disabled={createMut.isPending}
								data-testid="commission-preview-publisher-input"
							/>
						</div>
						<div className="flex flex-col gap-1.5">
							<label htmlFor="cp-rule" className={LABEL}>
								Rule
							</label>
							<select
								id="cp-rule"
								className={INPUT}
								value={rule}
								onChange={(e) => setRule(e.currentTarget.value)}
								disabled={createMut.isPending}
								data-testid="commission-preview-rule-select"
							>
								<option value="flat">Flat</option>
								<option value="percent">Percent</option>
								<option value="tiered">Tiered</option>
								<option value="hybrid">Hybrid</option>
							</select>
						</div>
					</div>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
						<div className="flex flex-col gap-1.5">
							<label htmlFor="cp-base" className={LABEL}>
								Base (USD)
							</label>
							<input
								id="cp-base"
								type="number"
								step="0.01"
								className={INPUT}
								value={baseStr}
								onChange={(e) => setBaseStr(e.currentTarget.value)}
								disabled={createMut.isPending}
								data-testid="commission-preview-base-input"
							/>
						</div>
						<div className="flex flex-col gap-1.5">
							<label htmlFor="cp-tier" className={LABEL}>
								Tier (USD)
							</label>
							<input
								id="cp-tier"
								type="number"
								step="0.01"
								className={INPUT}
								value={tierStr}
								onChange={(e) => setTierStr(e.currentTarget.value)}
								disabled={createMut.isPending}
								data-testid="commission-preview-tier-input"
							/>
						</div>
						<div className="flex flex-col gap-1.5">
							<label htmlFor="cp-adj" className={LABEL}>
								Adjustment (USD)
							</label>
							<input
								id="cp-adj"
								type="number"
								step="0.01"
								className={INPUT}
								value={adjStr}
								onChange={(e) => setAdjStr(e.currentTarget.value)}
								disabled={createMut.isPending}
								data-testid="commission-preview-adj-input"
							/>
						</div>
					</div>

					{/* Calc breakdown */}
					<div
						className="grid grid-cols-2 gap-2 rounded-[6px] border border-green-200 bg-green-50 p-3 text-sm sm:grid-cols-4"
						data-testid="commission-preview-breakdown"
					>
						<div className="flex flex-col">
							<span className={LABEL}>Base</span>
							<span
								className="font-mono text-zinc-900"
								data-testid="commission-preview-calc-base"
							>
								{formatMoneyCents(calc.base_cents)}
							</span>
						</div>
						<div className="flex flex-col">
							<span className={LABEL}>Tier</span>
							<span
								className="font-mono text-zinc-900"
								data-testid="commission-preview-calc-tier"
							>
								{formatMoneyCents(calc.tier_cents)}
							</span>
						</div>
						<div className="flex flex-col">
							<span className={LABEL}>Adjustment</span>
							<span
								className="font-mono text-zinc-900"
								data-testid="commission-preview-calc-adjustment"
							>
								{formatMoneyCents(calc.adjustment_cents)}
							</span>
						</div>
						<div className="flex flex-col">
							<span className={LABEL}>Total</span>
							<span
								className="font-mono font-semibold text-green-700"
								data-testid="commission-preview-calc-total"
							>
								{formatMoneyCents(calc.total_cents)}
							</span>
						</div>
					</div>

					<div className="flex items-center justify-between">
						{createMut.error ? (
							<p
								className="text-xs text-red-600"
								role="alert"
								data-testid="commission-preview-submit-error"
							>
								{createMut.error.message}
							</p>
						) : (
							<p className="text-xs text-zinc-500">
								Preview is computed locally; saving calls /api/v1/commission-preview.
							</p>
						)}
						<div className="flex items-center gap-2">
							<button
								type="button"
								className={BTN_GHOST}
								onClick={() => {
									setBaseStr("0.00");
									setTierStr("0.00");
									setAdjStr("0.00");
								}}
								disabled={createMut.isPending}
								data-testid="commission-preview-reset"
							>
								Reset
							</button>
							<button
								type="submit"
								className={BTN_PRIMARY}
								disabled={!canRun}
								data-testid="commission-preview-submit"
							>
								<PlayIcon />
								{createMut.isPending ? "Saving." : "Save Preview"}
							</button>
						</div>
					</div>
				</form>
			</section>

			{/* Table */}
			<section className={`${CARD} overflow-hidden`} aria-label="Preview results">
				<div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
					<h2 className="text-sm font-semibold text-zinc-800">Preview Runs</h2>
					<span
						className="text-xs text-zinc-500"
						data-testid="commission-preview-count"
					>
						{filtered.length} of {items.length}
					</span>
				</div>

				{listQuery.isLoading ? (
					<p
						className="px-5 py-6 text-sm text-zinc-500"
						data-testid="commission-preview-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="px-5 py-6 text-sm text-red-600"
						role="alert"
						data-testid="commission-preview-error"
					>
						{listQuery.error.message}
					</p>
				) : filtered.length === 0 ? (
					<p
						className="px-5 py-6 text-sm text-zinc-500"
						data-testid="commission-preview-empty"
					>
						No preview runs yet. Fill in the form and click Run Preview.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse text-sm"
							data-testid="commission-preview-table"
						>
							<thead className="bg-zinc-50">
								<tr>
									<th className={TH}>Campaign</th>
									<th className={TH}>Rule</th>
									<th className={TH}>Calc</th>
									<th className={`${TH} text-right`}>Preview Amount</th>
									<th className={TH}>Status</th>
								</tr>
							</thead>
							<tbody>
								{filtered.map((row) => (
									<tr
										key={row.id}
										data-testid={`commission-preview-row-${row.id}`}
										className="hover:bg-green-50/40"
									>
										<td className={TD}>
											<span className="font-medium text-zinc-900">
												{row.campaign ?? "-"}
											</span>
											<span className="ml-2 text-xs text-zinc-500">
												{row.publisher ?? ""}
											</span>
										</td>
										<td className={TD}>{row.rule ?? "flat"}</td>
										<td className={`${TD} font-mono text-xs text-zinc-600`}>
											{rowCalcString(row)}
										</td>
										<td
											className={`${TD} text-right font-mono font-semibold text-green-700`}
										>
											{formatMoneyCents(row.total_cents ?? 0)}
										</td>
										<td className={TD}>
											<span className={statusPill(String(row.status ?? ""))}>
												{row.status ?? "pending"}
											</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default CommissionPreviewPage;
